import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PersonaAutocomplete from "./InfoInvoice/PersonaAutocomplete";
import Ncredit from "./InfoInvoice/Ncredit";

const tdLabelSx = {
  width: 200,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj ?? {});
  } catch {
    return "";
  }
};

const buildPersonaFromInitial = (initialData) => {
  if (!initialData) return null;

  if (initialData.persona && typeof initialData.persona === "object") {
    return {
      id:
        initialData.persona.id ||
        initialData.persona.identificacion ||
        initialData.persona.razon_social ||
        "",
      identificacion: String(initialData.persona.identificacion || "").trim(),
      razon_social: String(initialData.persona.razon_social || "").trim(),
      correo: String(initialData.persona.correo || "").trim(),
      telefono: String(initialData.persona.telefono || "").trim(),
      direccion: String(initialData.persona.direccion || "").trim(),
    };
  }

  const identificacion = String(initialData.identificacion_cliente || "").trim();
  const razon_social = String(initialData.cliente || "").trim();
  if (!identificacion && !razon_social) return null;

  return {
    id: identificacion || razon_social,
    identificacion,
    razon_social,
    correo: String(initialData.email || "").trim(),
    telefono: String(initialData.telefono || "").trim(),
    direccion: String(initialData.direccion || "").trim(),
  };
};

const comprobanteToBackend = (label) => {
  if (label === "Nota de Crédito") return "NOTA_CREDITO";
  return "FACTURA";
};

export default function InfoInvoice({ onChange, initialData, onProductsLoaded }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState({
    fecha_emision: "",
    tipo_documento: "Cliente",
    tipo_persona: "CLIENTE",
    tipo_comprobante: "Factura",
    puntos_emision: [],
    establecimiento: "",
    punto_emision: "",
    numero_documento: "",
    persona: null,
    referencia: "",
    vendedor: "",
    vencimiento_dias: "0",
    entregado: true,
    factura_relacionada_id: "",
    factura_relacionada_numero: "",
    factura_relacionada_clave_acceso: "",
    nota_credito_motivo_tipo: "DEVOLUCION",
    es_devolucion: true,
    es_ajuste: false,
  });

  const [fechaError, setFechaError] = useState(false);
  const [vendedores, setVendedores] = useState([]);

  const hydratingRef = useRef(false);
  const lastInitialSigRef = useRef("");

  const isNotaCredito = data.tipo_comprobante === "Nota de Crédito";

  useEffect(() => {
    const sig = safeStringify(initialData);
    if (!initialData) return;
    if (sig && sig === lastInitialSigRef.current) return;

    lastInitialSigRef.current = sig;
    hydratingRef.current = true;

    const personaPrefill = buildPersonaFromInitial(initialData);

    const motivoTipo =
      initialData.nota_credito_motivo_tipo ||
      (initialData.es_ajuste ? "AJUSTE" : "DEVOLUCION");

    setData((prev) => ({
      ...prev,
      ...initialData,
      puntos_emision: Array.isArray(initialData.puntos_emision)
        ? initialData.puntos_emision
        : prev.puntos_emision,
      persona: personaPrefill ?? prev.persona,
      tipo_persona: initialData.tipo_persona || initialData.tipo_documento || prev.tipo_persona,
      tipo_comprobante: initialData.tipo_comprobante || prev.tipo_comprobante,
      referencia: initialData.referencia ?? prev.referencia,
      vendedor: initialData.vendedor ?? prev.vendedor,
      establecimiento: initialData.establecimiento ?? prev.establecimiento,
      punto_emision: initialData.punto_emision ?? prev.punto_emision,
      numero_documento: initialData.numero_documento ?? prev.numero_documento,
      fecha_emision: initialData.fecha_emision ?? prev.fecha_emision,
      vencimiento_dias:
        initialData.vencimiento_dias != null
          ? String(initialData.vencimiento_dias)
          : prev.vencimiento_dias,
      entregado:
        typeof initialData.entregado === "boolean" ? initialData.entregado : prev.entregado,
      factura_relacionada_id:
        initialData.factura_relacionada_id != null
          ? String(initialData.factura_relacionada_id)
          : prev.factura_relacionada_id,
      factura_relacionada_numero:
        initialData.factura_relacionada_numero ?? prev.factura_relacionada_numero,
      factura_relacionada_clave_acceso:
        initialData.factura_relacionada_clave_acceso ?? prev.factura_relacionada_clave_acceso,
      nota_credito_motivo_tipo: motivoTipo,
      es_devolucion: motivoTipo === "DEVOLUCION",
      es_ajuste: motivoTipo === "AJUSTE",
    }));

    setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  }, [initialData]);

  useEffect(() => {
    const cargarVendedores = async () => {
      try {
        const r = await fetch("/facturas/usuarios/activos/", {
          credentials: "include",
        });
        const j = await r.json().catch(() => ({}));
        const lista = r.ok && Array.isArray(j.vendedores) ? j.vendedores : [];
        setVendedores(lista);

        if (lista.length > 0) {
          setData((prev) => {
            if (prev.vendedor !== undefined && prev.vendedor !== null && String(prev.vendedor) !== "") {
              return prev;
            }
            return {
              ...prev,
              vendedor: String(lista[0].id),
            };
          });
        }
      } catch {
        setVendedores([]);
      }
    };

    cargarVendedores();
  }, []);

  useEffect(() => {
    const cargarDetalle = async () => {
      try {
        const tipoDocumentoBackend = comprobanteToBackend(data.tipo_comprobante);
        const res = await fetch(
          `/facturas/documento/detalle/?tipo_documento=${encodeURIComponent(tipoDocumentoBackend)}`,
          {
            credentials: "include",
          }
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return;

        const puntos = Array.isArray(json.puntos_emision) ? json.puntos_emision : [];

        hydratingRef.current = true;

        setData((prev) => {
          const puntoActual = puntos.find(
            (p) =>
              String(p.establecimiento || "") === String(prev.establecimiento || "") &&
              String(p.punto_emision || "") === String(prev.punto_emision || "")
          );

          const primero = puntos[0] || {};

          return {
            ...prev,
            fecha_emision: prev.fecha_emision || json.fecha_emision || "",
            tipo_comprobante: json.tipo_comprobante || prev.tipo_comprobante,
            puntos_emision: puntos,
            establecimiento: puntoActual?.establecimiento || primero.establecimiento || "",
            punto_emision: puntoActual?.punto_emision || primero.punto_emision || "",
            numero_documento: puntoActual?.numero_documento || primero.numero_documento || "",
          };
        });

        setTimeout(() => {
          hydratingRef.current = false;
        }, 0);
      } catch {}
    };

    cargarDetalle();
  }, [data.tipo_comprobante]);

  useEffect(() => {
    if (hydratingRef.current) return;
    onChange?.(data);
  }, [data, onChange]);

  const patchData = (patch) => {
    setData((prev) => ({ ...prev, ...patch }));
  };

  const handleFechaChange = (e) => {
    const value = e.target.value;
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    setFechaError(value ? !regex.test(value) : false);
    patchData({ fecha_emision: value });
  };

  const handlePuntoChange = (e) => {
    const seleccionado = e.target.value;
    const punto = data.puntos_emision.find((p) => p.punto_emision === seleccionado);
    patchData({
      establecimiento: punto ? punto.establecimiento : data.establecimiento,
      punto_emision: seleccionado,
      numero_documento: punto ? punto.numero_documento : "",
    });
  };

  const handleTipoComprobanteChange = (e) => {
    const nuevoTipo = e.target.value;

    hydratingRef.current = true;

    setData((prev) => ({
      ...prev,
      tipo_comprobante: nuevoTipo,
      puntos_emision: [],
      establecimiento: "",
      punto_emision: "",
      numero_documento: "",
      factura_relacionada_id: "",
      factura_relacionada_numero: "",
      factura_relacionada_clave_acceso: "",
      nota_credito_motivo_tipo: "DEVOLUCION",
      es_devolucion: true,
      es_ajuste: false,
    }));

    setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  };

  if (isMobile) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#333" }}>
          Información del Documento
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Fecha de Emisión
            </Typography>
            <TextField
              fullWidth
              value={data.fecha_emision}
              onChange={handleFechaChange}
              error={fechaError}
              helperText={fechaError ? "Formato inválido (dd/mm/aaaa)" : ""}
              placeholder="dd/mm/aaaa"
              inputProps={{ maxLength: 10 }}
            />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Tipo de Documento
            </Typography>
            <Select
              fullWidth
              value={data.tipo_persona}
              onChange={(e) =>
                patchData({
                  tipo_persona: e.target.value,
                  persona: null,
                  factura_relacionada_id: "",
                  factura_relacionada_numero: "",
                  factura_relacionada_clave_acceso: "",
                })
              }
            >
              <MenuItem value="CLIENTE">Cliente</MenuItem>
              <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Tipo de Comprobante
            </Typography>
            <Select fullWidth value={data.tipo_comprobante} onChange={handleTipoComprobanteChange}>
              <MenuItem value="Factura">Factura</MenuItem>
              <MenuItem value="Nota de Crédito">Nota de Crédito</MenuItem>
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Establecimiento
            </Typography>
            <TextField fullWidth value={data.establecimiento} InputProps={{ readOnly: true }} />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              P. Emisión
            </Typography>
            <Select fullWidth value={data.punto_emision} onChange={handlePuntoChange}>
              {data.puntos_emision.map((p, i) => (
                <MenuItem key={`${p.establecimiento}-${p.punto_emision}-${i}`} value={p.punto_emision}>
                  {p.punto_emision}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              # de Documento
            </Typography>
            <TextField fullWidth value={data.numero_documento} InputProps={{ readOnly: true }} />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Persona
            </Typography>
            <PersonaAutocomplete
              value={data.persona}
              tipoDocumento={data.tipo_persona}
              onChange={(persona) =>
                patchData({
                  persona,
                  factura_relacionada_id: "",
                  factura_relacionada_numero: "",
                  factura_relacionada_clave_acceso: "",
                })
              }
              mobile
            />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Referencia
            </Typography>
            <TextField
              fullWidth
              value={data.referencia}
              onChange={(e) => patchData({ referencia: e.target.value })}
            />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Vendedor
            </Typography>
            <Select
              fullWidth
              value={data.vendedor}
              onChange={(e) =>
                patchData({
                  vendedor: e.target.value,
                })
              }
            >
              {vendedores.map((v) => (
                <MenuItem key={v.id} value={String(v.id)}>
                  {v.nombre}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: isNotaCredito ? "1px solid #e0e0e0" : "none" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Vencimiento
            </Typography>
            <TextField
              fullWidth
              value={data.vencimiento_dias}
              onChange={(e) =>
                patchData({
                  vencimiento_dias: e.target.value.replace(/\D/g, "").slice(0, 3) || "0",
                })
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <Checkbox
                checked={data.entregado}
                onChange={(e) =>
                  patchData({
                    entregado: e.target.checked,
                  })
                }
              />
              <Typography sx={{ fontSize: 14, color: "#333" }}>Entregado</Typography>
            </Box>
          </Box>

          {isNotaCredito && (
            <Box sx={{ p: 1.25 }}>
              <Ncredit
                persona={data.persona}
                facturaRelacionadaId={data.factura_relacionada_id}
                facturaRelacionadaNumero={data.factura_relacionada_numero}
                facturaRelacionadaClaveAcceso={data.factura_relacionada_clave_acceso}
                motivoTipo={data.nota_credito_motivo_tipo}
                onChange={({
                  factura_relacionada_id,
                  factura_relacionada_numero,
                  factura_relacionada_clave_acceso,
                  nota_credito_motivo_tipo,
                  es_devolucion,
                  es_ajuste,
                }) =>
                  patchData({
                    factura_relacionada_id,
                    factura_relacionada_numero,
                    factura_relacionada_clave_acceso,
                    nota_credito_motivo_tipo,
                    es_devolucion,
                    es_ajuste,
                  })
                }
                onProductsLoaded={onProductsLoaded}
                mobile
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#333" }}>
        Información del Documento
      </Typography>

      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.fecha_emision}
                onChange={handleFechaChange}
                error={fechaError}
                helperText={fechaError ? "Formato inválido (dd/mm/aaaa)" : ""}
                placeholder="dd/mm/aaaa"
                inputProps={{ maxLength: 10 }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Tipo de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.tipo_persona}
                onChange={(e) =>
                  patchData({
                    tipo_persona: e.target.value,
                    persona: null,
                    factura_relacionada_id: "",
                    factura_relacionada_numero: "",
                    factura_relacionada_clave_acceso: "",
                  })
                }
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="CLIENTE">Cliente</MenuItem>
                <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
              </Select>
            </TableCell>

            <TableCell sx={{ ...tdFieldSx, pl: 2 }}>
              <Select
                value={data.tipo_comprobante}
                onChange={handleTipoComprobanteChange}
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="Factura">Factura</MenuItem>
                <MenuItem value="Nota de Crédito">Nota de Crédito</MenuItem>
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Establecimiento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.establecimiento}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>P. Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select value={data.punto_emision} onChange={handlePuntoChange} sx={{ minWidth: 280 }}>
                {data.puntos_emision.map((p, i) => (
                  <MenuItem key={`${p.establecimiento}-${p.punto_emision}-${i}`} value={p.punto_emision}>
                    {p.punto_emision}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}># de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.numero_documento}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Persona:</TableCell>
            <TableCell sx={tdFieldSx}>
              <PersonaAutocomplete
                value={data.persona}
                tipoDocumento={data.tipo_persona}
                onChange={(persona) =>
                  patchData({
                    persona,
                    factura_relacionada_id: "",
                    factura_relacionada_numero: "",
                    factura_relacionada_clave_acceso: "",
                  })
                }
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Referencia:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.referencia}
                onChange={(e) => patchData({ referencia: e.target.value })}
                sx={{ minWidth: 350 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.vendedor}
                onChange={(e) =>
                  patchData({
                    vendedor: e.target.value,
                  })
                }
                sx={{ minWidth: 280 }}
              >
                {vendedores.map((v) => (
                  <MenuItem key={v.id} value={String(v.id)}>
                    {v.nombre}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vencimiento / Entregado:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  value={data.vencimiento_dias}
                  onChange={(e) =>
                    patchData({
                      vencimiento_dias: e.target.value.replace(/\D/g, "").slice(0, 3) || "0",
                    })
                  }
                  sx={{ width: 120 }}
                />
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={data.entregado}
                    onChange={(e) =>
                      patchData({
                        entregado: e.target.checked,
                      })
                    }
                  />
                  <Typography>Entregado</Typography>
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          {isNotaCredito && (
            <TableRow>
              <TableCell sx={tdLabelSx}>Documento Relacionado:</TableCell>
              <TableCell sx={tdFieldSx}>
                <Ncredit
                  persona={data.persona}
                  facturaRelacionadaId={data.factura_relacionada_id}
                  facturaRelacionadaNumero={data.factura_relacionada_numero}
                  facturaRelacionadaClaveAcceso={data.factura_relacionada_clave_acceso}
                  motivoTipo={data.nota_credito_motivo_tipo}
                  onChange={({
                    factura_relacionada_id,
                    factura_relacionada_numero,
                    factura_relacionada_clave_acceso,
                    nota_credito_motivo_tipo,
                    es_devolucion,
                    es_ajuste,
                  }) =>
                    patchData({
                      factura_relacionada_id,
                      factura_relacionada_numero,
                      factura_relacionada_clave_acceso,
                      nota_credito_motivo_tipo,
                      es_devolucion,
                      es_ajuste,
                    })
                  }
                  onProductsLoaded={onProductsLoaded}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}